import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

// Documentation structure for EasyChat AI Platform
const DOCS_DATA = {
  introduction: {
    title: "Introduction",
    topics: [
      {
        id: "overview",
        title: "Overview",
        sections: [
          { id: "intro", title: "Welcome to EasyChat" },
          { id: "key-features", title: "Key Capabilities & Differentiators" },
          { id: "platform-modules", title: "Product Surface & Functional Modules" },
          { id: "getting-help", title: "Getting Help & Support" }
        ]
      },
      {
        id: "how-works",
        title: "How EasyChat Works",
        sections: [
          { id: "chat-flow", title: "Multi-Channel Message Lifecycle" },
          { id: "streaming", title: "Low-Latency Realtime Streaming (SSE)" },
          { id: "runtime-routing", title: "Runtime Routing & Decision Layer" }
        ]
      },
      {
        id: "core-concepts",
        title: "Core Concepts",
        sections: [
          { id: "concepts-agents", title: "AI Agents & Personas" },
          { id: "concepts-rag", title: "Knowledge Base & RAG Retrieval" },
          { id: "concepts-channels", title: "Omnichannel Integrations" },
          { id: "concepts-workspaces", title: "Profiles, Isolation & Multi-Tenancy" }
        ]
      },
      {
        id: "architecture",
        title: "Architecture",
        sections: [
          { id: "system-stack", title: "System Stack Overview" },
          { id: "data-privacy", title: "Data Privacy & Compliance" },
          { id: "ops-observability", title: "Observability, Governance & Operations" }
        ]
      },
      {
        id: "data-deletion",
        title: "Data Deletion Instructions",
        sections: [
          { id: "data-deletion-instructions", title: "Data Deletion Instructions" }
        ]
      }
    ]
  },
  "getting-started": {
    title: "Getting Started",
    topics: [
      {
        id: "quickstart",
        title: "Quickstart",
        sections: [
          { id: "qs-intro", title: "10-Minute Onboarding Path" },
          { id: "qs-requirements", title: "Prerequisites & Environment Setup" },
          { id: "qs-access-profile", title: "Active Workspace & API Credentials" }
        ]
      },
      {
        id: "agent-setup",
        title: "Create Your First Agent",
        sections: [
          { id: "agent-setup-step", title: "Defining Instructions & Persona" },
          { id: "agent-prompting", title: "Instruction Optimizer" },
          { id: "agent-publish", title: "Publishing & Activating Agents" }
        ]
      },
      {
        id: "channel-setup",
        title: "Connect a Channel",
        sections: [
          { id: "channel-whatsapp", title: "Connecting WhatsApp Cloud API" },
          { id: "channel-widget", title: "Embedding Website Chat Widget" }
        ]
      },
      {
        id: "testing",
        title: "Testing & Debugging",
        sections: [
          { id: "chat-testing", title: "Interactive Chat Playground" },
          { id: "session-logs", title: "Reviewing Conversation Transcripts" },
          { id: "agent-debug", title: "Inspecting Tool & Prompt Traces" }
        ]
      },
      {
        id: "tools-quick",
        title: "Registering a Tool",
        sections: [
          { id: "tool-reg-step", title: "Custom REST API Endpoint Definition" },
          { id: "tool-inv-step", title: "Testing Autonomous Tool Invocations" }
        ]
      }
    ]
  },
  agents: {
    title: "AI Agents",
    topics: [
      {
        id: "definition",
        title: "Agent Definition",
        sections: [
          { id: "agents-definition", title: "Agent Core Architecture" },
          { id: "agents-profile", title: "Profile Binding & Model Configuration" },
          { id: "agents-lifecycle", title: "Message Pipeline & State Machine" }
        ]
      },
      {
        id: "configuration",
        title: "Configuration",
        sections: [
          { id: "config-basic", title: "Identity, Tone & Behavioral Guardrails" },
          { id: "config-advanced", title: "Context Window & Memory Limits" },
          { id: "config-engine", title: "Multi-Language & Localization" }
        ]
      },
      {
        id: "models",
        title: "Model Providers",
        sections: [
          { id: "llm-providers", title: "Supported Providers (OpenAI, Anthropic, Gemini, Groq)" },
          { id: "llm-models", title: "Selecting Specialized Inference Engines" },
          { id: "llm-hyperparams", title: "Temperature, Max Tokens & Top-P Tuning" }
        ]
      },
      {
        id: "rag-attachment",
        title: "Knowledge & RAG",
        sections: [
          { id: "rag-linking", title: "Attaching Vector Databases to Agents" },
          { id: "rag-config", title: "Retrieval Confidence Thresholds" },
          { id: "rag-behavior", title: "Handling Out-of-Scope Queries" }
        ]
      },
      {
        id: "flows-skills",
        title: "Flows & Skills",
        sections: [
          { id: "flows-overview", title: "Step-by-Step Procedural Business Flows" },
          { id: "skills-overview", title: "Modular SKILL.md Knowledge Packs" }
        ]
      },
      {
        id: "human-handoff",
        title: "Human Handoff & Routing",
        sections: [
          { id: "handoff-routing", title: "Escalation to Operator Pools" },
          { id: "smart-routing", title: "Smart Routing (AI Team Assignment)" },
          { id: "after-hours-support", title: "After-Hours Support (24/7 Coverage)" },
          { id: "handoff-interface", title: "Live Human Agent Chat Interface" }
        ]
      }
    ]
  },
  "knowledge-base": {
    title: "Knowledge Base",
    topics: [
      {
        id: "sources",
        title: "Sources & Documents",
        sections: [
          { id: "sources-types", title: "Supported Formats (PDF, DOCX, TXT, MD, CSV)" },
          { id: "docs-upload", title: "Batch Document Uploads" },
          { id: "sources-crawler", title: "Web Crawler & URL Ingestion" }
        ]
      },
      {
        id: "chunking",
        title: "Chunking & Embeddings",
        sections: [
          { id: "rag-chunking", title: "Splitting Strategies (Recursive, Semantic, Fixed)" },
          { id: "config-chunksize", title: "Target Chunk Size & Overlap Ratios" },
          { id: "rag-retrieval", title: "Hybrid Vector Search & Re-Ranking" }
        ]
      },
      {
        id: "knowledge-graph",
        title: "Knowledge Graph",
        sections: [
          { id: "kg-extraction", title: "Automated Entity & Relationship Extraction" },
          { id: "kg-visualization", title: "Interactive Graph Visualization" }
        ]
      },
      {
        id: "kb-sync",
        title: "Automated Sync (KB Cron)",
        sections: [
          { id: "kb-sync-overview", title: "Document Synchronization Overview" },
          { id: "kb-sync-freshdesk", title: "Freshdesk Knowledge Base Integration" },
          { id: "kb-sync-deduplication", title: "Smart Article Deduplication" }
        ]
      }
    ]
  },
  tools: {
    title: "Tools & MCP",
    topics: [
      {
        id: "overview-tools",
        title: "Overview",
        sections: [
          { id: "tools-intro", title: "Autonomous Action Execution" },
          { id: "tools-schema", title: "JSON Schema Parameter Specifications" }
        ]
      },
      {
        id: "custom-api-tools",
        title: "Custom REST Tools",
        sections: [
          { id: "custom-rest", title: "Configuring Endpoints, Methods & Headers" },
          { id: "custom-params", title: "LLM Prompt vs Constant Arguments" },
          { id: "otp-verification", title: "OTP PIN Code Validation Protocol" }
        ]
      },
      {
        id: "mcp-protocol",
        title: "Model Context Protocol",
        sections: [
          { id: "mcp-overview", title: "Connecting SSE Bridge Servers" },
          { id: "mcp-tools-discovery", title: "Automatic Schema Discovery & Invocation" }
        ]
      }
    ]
  },
  channels: {
    title: "Channels & Integrations",
    topics: [
      {
        id: "channel-whatsapp",
        title: "WhatsApp Cloud API",
        sections: [
          { id: "wa-setup", title: "Meta Developer App & Phone Number ID" },
          { id: "wa-webhooks", title: "Configuring Inbound Webhooks & Verify Tokens" }
        ]
      },
      {
        id: "channel-telegram",
        title: "Telegram Bot",
        sections: [
          { id: "tg-setup", title: "BotFather API Token Configuration" },
          { id: "tg-features", title: "Direct Chat Routing & Media Handlers" }
        ]
      },
      {
        id: "channel-slack",
        title: "Slack App",
        sections: [
          { id: "slack-setup", title: "Slack App Manifest, Scopes & Tokens" },
          { id: "slack-events", title: "Channel Mentions & Direct Messages" }
        ]
      },
      {
        id: "channel-widget",
        title: "Website Chat Widget",
        sections: [
          { id: "widget-embed", title: "JavaScript Embed Snippet" },
          { id: "widget-customization", title: "Theme, Branding & Positioning" }
        ]
      },
      {
        id: "crm-integrations",
        title: "Freshdesk & Freshchat",
        sections: [
          { id: "fd-setup", title: "Freshdesk Ticket Sync & Escalations" },
          { id: "fc-setup", title: "Freshchat Human Operator Routing" }
        ]
      }
    ]
  },
  campaigns: {
    title: "Campaigns & Triggers",
    topics: [
      {
        id: "campaigns-overview",
        title: "Overview",
        sections: [
          { id: "camp-intro", title: "Proactive Engagement Automation" },
          { id: "camp-priority", title: "Priority Orchestration (P0 - P10)" }
        ]
      },
      {
        id: "triggers",
        title: "Behavioral Triggers",
        sections: [
          { id: "trig-events", title: "Exit Intent, Scroll Depth & Time Delay" },
          { id: "trig-urls", title: "Page URL Pattern Matching" }
        ]
      },
      {
        id: "segments",
        title: "Audience & Segments",
        sections: [
          { id: "seg-rules", title: "Filtering Contacts by Behavior & Tags" },
          { id: "seg-templates", title: "Dynamic Variable Message Templates" }
        ]
      }
    ]
  },
  webhooks: {
    title: "Webhooks",
    topics: [
      {
        id: "wh-overview",
        title: "Inbound Webhooks",
        sections: [
          { id: "wh-intro", title: "Webhook Architecture in EasyChat" },
          { id: "wh-auth", title: "API Keys & Bearer Token Authentication" },
          { id: "wh-payload", title: "Standard JSON Request Payload Format" }
        ]
      },
      {
        id: "wh-events",
        title: "Outbound Callbacks",
        sections: [
          { id: "event-types", title: "Session & Message Lifecycle Events" },
          { id: "event-retry", title: "Exponential Backoff & Delivery Guarantees" }
        ]
      }
    ]
  },
    "api-reference": {
    title: "API Reference",
    topics: [
      {
        id: "api-overview",
        title: "Overview & Auth",
        sections: [
          { id: "api-auth-headers", title: "Authentication & Bearer Tokens" },
          { id: "api-swagger-ui", title: " Interactive Swagger & OpenAPI" }
        ]
      },
      {
        id: "api-agents",
        title: "AI Agents",
        sections: [
          { id: "api-agents-list", title: "GET /api/ai-agents — List All Agents" },
          { id: "api-agents-create", title: "POST /api/ai-agents — Create Agent" },
          { id: "api-agents-get", title: "GET /api/ai-agents/{id} — Retrieve Agent Details" },
          { id: "api-agents-update", title: "PUT /api/ai-agents/{id} — Update Agent" },
          { id: "api-agents-delete", title: "DELETE /api/ai-agents/{id} — Delete Agent" }
        ]
      },
      {
        id: "api-chat",
        title: "Chat & Sessions",
        sections: [
          { id: "api-chat-send", title: "POST /api/chat — Send Message (SSE Stream / Sync)" },
          { id: "api-chat-history", title: "GET /api/chat/history/{session_id} — Fetch Session History" }
        ]
      },
      {
        id: "api-kb",
        title: "Knowledge Base",
        sections: [
          { id: "api-kb-upload", title: "POST /api/documents/upload — Upload & Ingest File" },
          { id: "api-kb-search", title: "POST /api/knowledge-base/search — Semantic Search" }
        ]
      },
      {
        id: "api-tools",
        title: "Tools & MCP",
        sections: [
          { id: "api-tools-list", title: "GET /api/api-tools — List Custom Tools" },
          { id: "api-tools-create", title: "POST /api/api-tools — Register Custom Tool" },
          { id: "api-mcp-status", title: "GET /api/mcp/servers — MCP Server Discovery" }
        ]
      },
      {
        id: "api-webhooks",
        title: "Webhooks",
        sections: [
          { id: "api-wh-incoming", title: "POST /api/webhook/incoming — Post Inbound Payload" },
          { id: "api-wh-keys", title: "GET & POST /api/webhooks/keys — Manage API Keys" }
        ]
      },
      {
        id: "api-campaigns",
        title: "Campaigns",
        sections: [
          { id: "api-camp-list", title: "GET /api/campaigns — List Campaigns" },
          { id: "api-camp-trigger", title: "POST /api/campaigns/{id}/trigger — Fire Campaign" }
        ]
      },
      {
        id: "api-contacts",
        title: "Contacts & Segments",
        sections: [
          { id: "api-contacts-list", title: "GET /api/contacts — List Contacts" },
          { id: "api-segments-list", title: "GET /api/contacts/segments — List Segments" }
        ]
      },
      {
        id: "api-handoff",
        title: "Human Handoff",
        sections: [
          { id: "api-handoff-pools", title: "GET /api/human-agent-groups — Operator Pools" },
          { id: "api-handoff-transfer", title: "POST /api/human-agents/transfer — Escalate Session" }
        ]
      },
      {
        id: "api-analytics",
        title: "Analytics",
        sections: [
          { id: "api-analytics-tokens", title: "GET /api/analytics/tokens — Token Consumption" },
          { id: "api-analytics-sessions", title: "GET /api/analytics/active-chats — Active Sessions" }
        ]
      },
      {
        id: "api-errors",
        title: "Status & Errors",
        sections: [
          { id: "api-error-codes", title: "HTTP Status Code Reference" },
          { id: "api-error-format", title: "Error Response Payload Structure" }
        ]
      }
    ]
  }
};

const Docs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mainScrollRef = useRef(null);
  const searchContainerRef = useRef(null);

  const rawApiBase = process.env.REACT_APP_API_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const apiBaseHost = rawApiBase.endsWith("/api") ? rawApiBase.slice(0, -4) : rawApiBase;
  const docsUrl = `${apiBaseHost}/api/docs`;
  const redocUrl = `${apiBaseHost}/api/redoc`;

  // Detect direct URL routing (e.g. /data-deletion or query params)
  const isDirectDataDeletion = location.pathname.includes("data-deletion") || searchParams.get("topic") === "data-deletion";

  const [activeTab, setActiveTab] = useState(isDirectDataDeletion ? "introduction" : (searchParams.get("tab") || "introduction"));
  const [activeSubTopicId, setActiveSubTopicId] = useState(isDirectDataDeletion ? "data-deletion" : (searchParams.get("topic") || "overview"));
  const [activeSectionId, setActiveSectionId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Sync state if pathname or search params change dynamically
  useEffect(() => {
    if (location.pathname.includes("data-deletion") || searchParams.get("topic") === "data-deletion") {
      setActiveTab("introduction");
      setActiveSubTopicId("data-deletion");
    } else {
      const tabParam = searchParams.get("tab");
      const topicParam = searchParams.get("topic");
      if (tabParam) setActiveTab(tabParam);
      if (topicParam) setActiveSubTopicId(topicParam);
    }
  }, [location.pathname, searchParams]);

  const activeTabData = DOCS_DATA[activeTab] || DOCS_DATA.introduction;
  const activeSubTopic =
    activeTabData.topics.find((t) => t.id === activeSubTopicId) ||
    activeTabData.topics[0];

  useEffect(() => {
    const data = DOCS_DATA[activeTab];
    if (data && data.topics.length > 0) {
      const exists = data.topics.some((t) => t.id === activeSubTopicId);
      if (!exists) {
        setActiveSubTopicId(data.topics[0].id);
      }
    }
  }, [activeTab, activeSubTopicId]);

  const allPages = useMemo(() => {
    const pages = [];
    Object.entries(DOCS_DATA).forEach(([tabKey, category]) => {
      category.topics.forEach((topic) => {
        pages.push({
          tabKey,
          tabTitle: category.title,
          topicId: topic.id,
          topicTitle: topic.title,
        });
      });
    });
    return pages;
  }, []);

  const currentPageIndex = useMemo(() => {
    return allPages.findIndex(
      (p) => p.tabKey === activeTab && p.topicId === activeSubTopic?.id
    );
  }, [allPages, activeTab, activeSubTopic]);

  const prevPage = currentPageIndex > 0 ? allPages[currentPageIndex - 1] : null;
  const nextPage =
    currentPageIndex >= 0 && currentPageIndex < allPages.length - 1
      ? allPages[currentPageIndex + 1]
      : null;

  const navigateToPage = (page) => {
    if (!page) return;
    setActiveTab(page.tabKey);
    setActiveSubTopicId(page.topicId);
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];

    const results = [];
    Object.entries(DOCS_DATA).forEach(([categoryKey, category]) => {
      category.topics.forEach((topic) => {
        if (topic.title.toLowerCase().includes(q)) {
          results.push({
            type: "Topic",
            title: topic.title,
            categoryKey,
            categoryTitle: category.title,
            topicId: topic.id,
            sectionId: topic.sections[0]?.id || "",
          });
        }
        topic.sections.forEach((section) => {
          if (section.title.toLowerCase().includes(q)) {
            results.push({
              type: "Section",
              title: section.title,
              categoryKey,
              categoryTitle: category.title,
              topicId: topic.id,
              sectionId: section.id,
            });
          }
        });
      });
    });
    return results;
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setIsSearchOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleSelectSearchResult = (result) => {
    setActiveTab(result.categoryKey);
    setActiveSubTopicId(result.topicId);
    setSearchQuery("");
    setIsSearchOpen(false);

    setTimeout(() => {
      if (result.sectionId) {
        const el = document.getElementById(result.sectionId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          setActiveSectionId(result.sectionId);
        }
      }
    }, 120);
  };

  useEffect(() => {
    const mainEl = mainScrollRef.current;
    if (!mainEl) return;

    const handleScroll = () => {
      const sections = mainEl.querySelectorAll("section");
      let currentActive = "";
      const mainRect = mainEl.getBoundingClientRect();

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const rect = section.getBoundingClientRect();
        if (rect.top - mainRect.top <= 120) {
          currentActive = section.id;
        }
      }

      if (currentActive) {
        setActiveSectionId(currentActive);
      } else if (sections.length > 0) {
        setActiveSectionId(sections[0].id);
      }
    };

    mainEl.addEventListener("scroll", handleScroll);
    setTimeout(handleScroll, 100);

    return () => {
      mainEl.removeEventListener("scroll", handleScroll);
    };
  }, [activeSubTopicId]);

  const handleTabChange = (tabKey) => {
    if (location.pathname.includes("data-deletion") && tabKey !== "introduction") {
      navigate(`/docs?tab=${tabKey}`);
    }
    setActiveTab(tabKey);
  };

  const handleScrollToSection = (sectionId) => {
    const mainEl = mainScrollRef.current;
    const element = document.getElementById(sectionId);
    if (mainEl && element) {
      const topOffset = element.offsetTop - 16;
      mainEl.scrollTo({ top: topOffset, behavior: "smooth" });
    }
  };

  // Content Renderers
  const renderIntroductionContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "intro":
        return (
          <div className="space-y-4 text-[14px]">
            <p className="text-zinc-600 leading-relaxed">
              <strong>EasyChat</strong> is an enterprise AI conversation platform designed for deploying autonomous, multi-channel customer engagement agents across WhatsApp, Telegram, Slack, Web Widgets, and custom APIs.
            </p>
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Core Capabilities</h4>
              <ul className="list-disc list-inside text-xs text-zinc-600 space-y-1">
                <li>Omnichannel messaging: Native connections to WhatsApp Cloud API, Telegram, Slack, and Web Widgets.</li>
                <li>Hybrid RAG Knowledge Base: Real-time semantic retrieval paired with interactive Knowledge Graphs.</li>
                <li>Business Flows & Skills: Decision-tree procedural logic with deterministic conditional routing.</li>
                <li>Extensible Action Layer: Custom REST tools, Model Context Protocol (MCP), and OTP verification.</li>
                <li>Human Handoff: Seamless escalation from AI agents to operator groups and live operator inboxes.</li>
              </ul>
            </div>
          </div>
        );
      case "key-features":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>EasyChat combines state-of-the-art LLMs with enterprise guardrails:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <div className="p-3.5 bg-white border border-zinc-200 rounded-xl">
                <h5 className="font-semibold text-zinc-900 mb-1">Multi-Model Engine</h5>
                <p className="text-zinc-500 text-[11px]">Integrate OpenAI, Anthropic Claude, Google Gemini, Groq, Ollama, and DeepSeek effortlessly.</p>
              </div>
              <div className="p-3.5 bg-white border border-zinc-200 rounded-xl">
                <h5 className="font-semibold text-zinc-900 mb-1">Low-Latency SSE</h5>
                <p className="text-zinc-500 text-[11px]">Stream responses token-by-token directly to web chat widgets and instant messenger clients.</p>
              </div>
            </div>
          </div>
        );
      case "chat-flow":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>Every message processed by EasyChat executes through a secure, pipeline-validated pipeline:</p>
            <ol className="list-decimal list-inside space-y-1.5 p-3 bg-zinc-50 rounded-xl border border-zinc-200 text-[11px]">
              <li><strong>Ingestion:</strong> Message webhook received via WhatsApp, Telegram, or Web Widget.</li>
              <li><strong>Session Identification:</strong> Context window loaded based on user ID and active conversation history.</li>
              <li><strong>Vector RAG Lookup:</strong> Query analyzed and matched against indexed document chunks and knowledge graphs.</li>
              <li><strong>Tool Evaluation:</strong> Model determines if an external API tool or MCP extension should execute.</li>
              <li><strong>Streaming Generation:</strong> Token response streamed to user while logging session metrics to analytics.</li>
            </ol>
          </div>
        );
      case "data-deletion-instructions":
      case "data-deletion":
        return (
          <div className="space-y-6 text-xs text-zinc-600">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-sm font-bold text-zinc-900 tracking-tight">
                EasyChat — Data Deletion Instructions
              </h4>
              <p className="text-zinc-600 leading-relaxed text-[13px]">
                EasyChat stores account details, WhatsApp Business Account connection data, contacts, message templates, and conversation history for businesses that use our platform.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="font-bold text-zinc-900 text-xs uppercase tracking-wider">
                To request deletion of your data:
              </h5>
              <div className="space-y-2.5">
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl flex items-start gap-3 shadow-2xs">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-bold shrink-0">
                    1
                  </span>
                  <div className="text-[12px] text-zinc-700 leading-relaxed">
                    Email <a href="mailto:info@kambaa.in" className="text-blue-600 font-semibold hover:underline">info@kambaa.in</a> from the email address linked to your EasyChat account, with the subject line: <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-zinc-900 font-mono text-[11px] font-semibold">"Data Deletion Request"</code>.
                  </div>
                </div>

                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl flex items-start gap-3 shadow-2xs">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-bold shrink-0">
                    2
                  </span>
                  <div className="text-[12px] text-zinc-700 leading-relaxed">
                    Include your registered account email and the business name or WhatsApp phone number connected to your account.
                  </div>
                </div>

                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl flex items-start gap-3 shadow-2xs">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-bold shrink-0">
                    3
                  </span>
                  <div className="text-[12px] text-zinc-700 leading-relaxed">
                    We will confirm your identity and delete your account data, including connected WhatsApp account details, contacts, and message history, within <strong>30 days</strong>. We will email you when deletion is complete.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50/70 border border-blue-200/80 rounded-xl space-y-1.5 text-[12px] text-zinc-700">
              <span className="font-bold text-blue-900 block">Self-Service WhatsApp Disconnection</span>
              <p className="leading-relaxed">
                You can also disconnect WhatsApp at any time under <strong>Integrations &gt; WhatsApp</strong>, and remove EasyChat from your Facebook account by going to <strong>Settings &gt; Business Integrations</strong> on Facebook and removing the app.
              </p>
            </div>

            <div className="p-4 bg-amber-50/60 border border-amber-200/80 rounded-xl text-[12px] text-amber-900 leading-relaxed font-medium">
              Some data may be kept where the law requires it.
            </div>

            <div className="pt-3 border-t border-zinc-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[12px] text-zinc-600">
              <div>
                <span className="font-semibold text-zinc-900">Direct Contact: </span>
                <a href="mailto:contact@kambaa.com" className="text-blue-600 hover:underline">contact@kambaa.com</a>
              </div>
              <div>
                <span className="font-semibold text-zinc-900">Phone: </span>
                <a href="tel:+917094490097" className="text-zinc-800 hover:underline font-mono">+91 70944 90097</a>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Detailed architecture and operating guidance for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong> in EasyChat.
          </p>
        );
    }
  };

  const renderGettingStartedContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "qs-intro":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <p>Set up an autonomous AI support assistant in 3 simple steps:</p>
            <div className="space-y-2">
              <div className="p-3 border border-zinc-200 rounded-xl bg-zinc-50/50">
                <strong className="text-zinc-900 block mb-0.5">1. Create an Agent</strong>
                <p className="text-zinc-500 text-[11px]">Define personality instructions, select your LLM model, and set confidence thresholds.</p>
              </div>
              <div className="p-3 border border-zinc-200 rounded-xl bg-zinc-50/50">
                <strong className="text-zinc-900 block mb-0.5">2. Upload Knowledge Documents</strong>
                <p className="text-zinc-500 text-[11px]">Upload company documentation (PDF, TXT, MD) to provide accurate grounded responses.</p>
              </div>
              <div className="p-3 border border-zinc-200 rounded-xl bg-zinc-50/50">
                <strong className="text-zinc-900 block mb-0.5">3. Connect Communication Channels</strong>
                <p className="text-zinc-500 text-[11px]">Deploy the agent to WhatsApp, Telegram, or embed the live web chat widget on your site.</p>
              </div>
            </div>
          </div>
        );
      case "channel-whatsapp":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>To connect WhatsApp Cloud API:</p>
            <div className="p-4 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] space-y-1 whitespace-pre-wrap break-words break-all">
              <div># 1. Provide Meta Phone Number ID & Access Token</div>
              <div># 2. Configure Webhook URL in Meta App Dashboard:</div>
              <div className="text-emerald-400">https://your-domain.com/api/webhook/whatsapp</div>
              <div># 3. Enter the Webhook Verification Token generated in WhatsApp Settings</div>
            </div>
          </div>
        );
      case "channel-widget":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>Embed the live chat widget on your website with this snippet before the closing <code>&lt;/body&gt;</code> tag:</p>
            <pre className="p-3 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] whitespace-pre-wrap break-words break-all">
{`<script src="https://your-domain.com/widget.js" 
  data-agent-id="YOUR_AGENT_ID" 
  data-color="#18181b">
</script>`}
            </pre>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Configuration guide for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

  const renderAgentContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "agents-definition":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              In EasyChat, an <strong>AI Agent</strong> is an autonomous conversation entity configured with specific system instructions, knowledge collections, custom tools, and escalation paths.
            </p>
            <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-xl space-y-1 text-[11px]">
              <div className="font-semibold text-zinc-900">Key Configuration Fields:</div>
              <div>• <strong>Instructions:</strong> Foundational system prompt defining role, rules, and tone.</div>
              <div>• <strong>Model:</strong> Inference engine (e.g., GPT-4o, Claude 3.5 Sonnet, Gemini 1.5 Pro).</div>
              <div>• <strong>Temperature:</strong> Creativity vs precision balance (0.0 to 1.0).</div>
              <div>• <strong>Human Handoff:</strong> Operator pool assignment for live chat transfers.</div>
            </div>
          </div>
        );
      case "handoff-routing":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              When an end user requests human assistance or sentiment indicates frustration, agents automatically route the session to operator pools configured in <strong>Human Agent Groups</strong>.
            </p>
          </div>
        );
      case "smart-routing":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-sm font-bold text-zinc-900 tracking-tight">
                What is Smart Routing?
              </h4>
              <p className="text-zinc-600 leading-relaxed text-[13px]">
                Smart Routing acts as an intelligent traffic controller for your customer support conversations. Instead of sending all customer issues to a single generic queue where staff have to sort through them manually, the AI automatically reads the conversation, figures out the customer's exact issue (like Billing, Technical Support, Returns, or Sales), and routes the chat directly to the specialized team equipped to resolve it.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="font-bold text-zinc-900 text-xs uppercase tracking-wider">
                How It Works Step-by-Step:
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">1</span>
                    <strong className="text-zinc-900 text-xs">Customer Needs Human Help</strong>
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    A visitor asks to speak with an agent, or their issue requires human expertise.
                  </p>
                </div>
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">2</span>
                    <strong className="text-zinc-900 text-xs">Context Analysis</strong>
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    The AI reviews the chat history to understand the visitor's core question or problem.
                  </p>
                </div>
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">3</span>
                    <strong className="text-zinc-900 text-xs">Team Evaluation</strong>
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    It matches the issue against your connected support departments (e.g. Freshchat or internal groups).
                  </p>
                </div>
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-zinc-900 text-white text-[10px] font-bold flex items-center justify-center">4</span>
                    <strong className="text-zinc-900 text-xs">Automated Handover & Fallback</strong>
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    If confident, it assigns the chat instantly. If uncertain, it safely sends it to the default team inbox.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-[12px] text-emerald-800 space-y-1">
              <strong>Business Benefit:</strong> Customers get connected to the right specialist on the first try without being passed between departments, cutting resolution times significantly.
            </div>
          </div>
        );
      case "after-hours-support":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-sm font-bold text-zinc-900 tracking-tight">
                What is After-Hours Support?
              </h4>
              <p className="text-zinc-600 leading-relaxed text-[13px]">
                After-Hours Support provides 24/7 automated coverage for your website and messaging channels when your human team is off the clock. Businesses define their operating schedule (such as 9:00 AM to 6:00 PM, Monday through Friday). Outside those hours, the AI automatically switches into a specialized after-hours mode to take care of customers.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="font-bold text-zinc-900 text-xs uppercase tracking-wider">
                How It Works Step-by-Step:
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <strong className="text-zinc-900 text-xs block">1. Define Your Work Hours</strong>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    In Widget Settings, configure active days, shift start/end times, and your local timezone.
                  </p>
                </div>
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <strong className="text-zinc-900 text-xs block">2. Automatic Shift Switching</strong>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    During work hours, normal AI + live agent handoff is active. After hours, the bot automatically transitions.
                  </p>
                </div>
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <strong className="text-zinc-900 text-xs block">3. Dedicated Off-Hours Persona</strong>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    Customize instructions for the night shift: answer FAQs using documents, inform users of office hours, and capture contact info.
                  </p>
                </div>
                <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1">
                  <strong className="text-zinc-900 text-xs block">4. Morning Follow-Up Queue</strong>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">
                    Customer queries and contact details are neatly logged so your team can follow up first thing in the morning.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-blue-50 border border-blue-200 rounded-xl text-[12px] text-blue-800 space-y-1">
              <strong>Business Benefit:</strong> Never miss a weekend lead or international prospect. Customers receive polite, immediate assistance around the clock instead of an empty chat window.
            </div>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Agent capabilities and settings for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

  const renderKnowledgeBaseContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "sources-types":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>EasyChat supports multiple ingest sources for grounding agent responses:</p>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                <span className="font-semibold text-zinc-900 block">PDF / DOCX Documents</span>
                Text extracted and parsed into clean semantic sections.
              </div>
              <div className="p-2.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                <span className="font-semibold text-zinc-900 block">Web URL Crawling</span>
                Recursive scraping of help documentation and FAQ pages.
              </div>
            </div>
          </div>
        );
      case "kg-extraction":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              The <strong>Knowledge Graph</strong> pipeline parses uploaded documents into structured entities (e.g. Products, Policies, Features) and relationships (e.g. COMPATIBLE_WITH, REQUIRES, PRICED_AT) to provide deterministic graph traversal alongside vector embeddings.
            </p>
          </div>
        );
      case "kb-sync-overview":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-sm font-bold text-zinc-900 tracking-tight">
                Automated Knowledge Base Synchronization (KB Cron)
              </h4>
              <p className="text-zinc-600 leading-relaxed text-[13px]">
                Document Synchronization automatically keeps your AI assistant's brain up-to-date with your latest external documents on a recurring schedule. Instead of team members having to manually download, re-format, and upload files every time an article changes, EasyChat automatically polls your data source and ingests new documents.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-4 bg-white border border-zinc-200 rounded-xl space-y-1.5">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">
                  Normal Flow
                </span>
                <h5 className="font-bold text-zinc-900 text-xs">Custom REST API Sync</h5>
                <p className="text-zinc-500 text-[11px] leading-relaxed">
                  Periodically polls your company's own document endpoint (returning IDs, titles, and download URLs) and incrementally imports new files.
                </p>
              </div>

              <div className="p-4 bg-white border border-zinc-200 rounded-xl space-y-1.5">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-900 text-white">
                  Integration Flow
                </span>
                <h5 className="font-bold text-zinc-900 text-xs">Freshdesk Solutions Sync</h5>
                <p className="text-zinc-500 text-[11px] leading-relaxed">
                  Directly connects to your Freshdesk knowledge base, pulling solution articles across categories and folders with automated ID deduplication.
                </p>
              </div>
            </div>
          </div>
        );
      case "kb-sync-freshdesk":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-sm font-bold text-zinc-900 tracking-tight">
                Freshdesk Knowledge Base Integration
              </h4>
              <p className="text-zinc-600 leading-relaxed text-[13px]">
                The Freshdesk integration links your customer helpdesk directly with EasyChat. When your support managers write or publish solution articles in Freshdesk, EasyChat synchronizes them automatically into your AI agent's knowledge base.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="font-bold text-zinc-900 text-xs uppercase tracking-wider">
                Flexible Import Scopes:
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 border border-zinc-200 rounded-xl bg-white space-y-1">
                  <strong className="text-zinc-900 text-xs">All Articles</strong>
                  <p className="text-zinc-500 text-[11px]">Discovers and imports every published solution article across your entire helpdesk.</p>
                </div>
                <div className="p-3 border border-zinc-200 rounded-xl bg-white space-y-1">
                  <strong className="text-zinc-900 text-xs">Category Scope</strong>
                  <p className="text-zinc-500 text-[11px]">Syncs all folders and articles inside a selected category (e.g. "Refund Policies").</p>
                </div>
                <div className="p-3 border border-zinc-200 rounded-xl bg-white space-y-1">
                  <strong className="text-zinc-900 text-xs">Folder Scope</strong>
                  <p className="text-zinc-500 text-[11px]">Targets a single specific folder (e.g. "Billing FAQs") and its nested articles.</p>
                </div>
                <div className="p-3 border border-zinc-200 rounded-xl bg-white space-y-1">
                  <strong className="text-zinc-900 text-xs">Specific Article</strong>
                  <p className="text-zinc-500 text-[11px]">Fetches a single article directly by ID or title keyword without traversing the hierarchy.</p>
                </div>
              </div>
            </div>
          </div>
        );
      case "kb-sync-deduplication":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-2">
              <h4 className="text-sm font-bold text-zinc-900 tracking-tight">
                Smart Article Deduplication
              </h4>
              <p className="text-zinc-600 leading-relaxed text-[13px]">
                To guarantee lightning-fast performance and prevent duplicate answers, EasyChat enforces ID-level deduplication during every sync.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="font-bold text-zinc-900 text-xs uppercase tracking-wider">
                How Deduplication Works:
              </h5>
              <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-2 text-[12px]">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-zinc-900">1. Pre-Check:</span>
                  <span>Before downloading full article text, EasyChat checks the unique Freshdesk Article ID against existing database records.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-zinc-900">2. Duplicate Skip:</span>
                  <span>If an article ID is already in the database, full content retrieval and vector embedding are completely skipped.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-zinc-900">3. Clean Ingestion:</span>
                  <span>Only brand-new articles are downloaded, cleaned of web HTML/headers, saved, and vectorized into the AI's search index.</span>
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-[12px] text-emerald-800 space-y-1">
              <strong>Result:</strong> Zero duplicate knowledge chunks, minimal network usage, and pure, uncluttered answers when users ask questions.
            </div>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Knowledge base indexing details for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

  const renderToolsContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "tools-intro":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              Tools give AI agents agency to interact with external databases, APIs, and systems during conversations.
            </p>
          </div>
        );
      case "otp-verification":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              For sensitive tools (e.g., executing refunds, modifying customer account data), enable <strong>OTP Verification</strong>. When triggered, the system pauses agent response generation and presents an interactive digit PIN code prompt to authenticate the user before calling the external API.
            </p>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Tool integration parameters for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

  const renderChannelsContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "wa-setup":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>Configure official WhatsApp Business messaging via Meta Cloud API:</p>
            <ul className="list-disc list-inside space-y-1 text-[11px]">
              <li>Obtain Phone Number ID and WhatsApp Business Account ID from Meta Developer console.</li>
              <li>Generate a Permanent System User Access Token with <code>whatsapp_business_messaging</code> permissions.</li>
              <li>Input credentials into <strong>WhatsApp Configuration</strong> and click Save.</li>
            </ul>
          </div>
        );
      case "tg-setup":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>Connect Telegram in seconds:</p>
            <ol className="list-decimal list-inside space-y-1 text-[11px]">
              <li>Message <code>@BotFather</code> on Telegram to create a new bot.</li>
              <li>Copy the HTTP API Token.</li>
              <li>Paste into <strong>Telegram Configuration</strong> to register the inbound webhook.</li>
            </ol>
          </div>
        );
      case "slack-setup":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              Connect your AI agent to Slack channels and direct messages using a Slack App with <code>chat:write</code> and <code>app_mentions:read</code> scopes.
            </p>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Channel integration guide for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

  const renderCampaignsContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "camp-intro":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              <strong>Campaigns</strong> enable proactive messaging triggers to engage visitors based on specific behaviors (exit intent, time on page, scroll percentage, specific URL visits).
            </p>
          </div>
        );
      case "camp-priority":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              Assign priority levels (P0 through P10). If multiple campaigns match a visitor's conditions, EasyChat resolves the conflict by firing the campaign with the highest priority first.
            </p>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Campaign parameters and trigger rules for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

  const renderWebhooksContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "wh-intro":
        return (
          <div className="space-y-3 text-xs text-zinc-600">
            <p>
              Integrate EasyChat directly into custom mobile apps, backends, or third-party platforms via Webhook APIs.
            </p>
            <pre className="p-3.5 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] whitespace-pre-wrap break-words break-all">
{`curl -X POST "https://your-domain.com/api/webhook/incoming" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "Hello AI!",
    "user_id": "customer_456",
    "session_id": "uuid-1234"
  }'`}
            </pre>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            Webhook delivery specifications for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };

    const renderApiContent = (sectionId, sectionTitle = "") => {
    switch (sectionId) {
      case "api-auth-headers":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <p>
              EasyChat uses standard JWT Bearer tokens for user & admin session authorization, and secret API keys for programmatic webhook ingest.
            </p>
            <div className="p-3.5 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] space-y-1 whitespace-pre-wrap break-words break-all">
              <div># Pass bearer token on all authenticated endpoints</div>
              <div className="text-zinc-400">Authorization: Bearer &lt;access_token&gt;</div>
              <div className="text-zinc-400">Content-Type: application/json</div>
            </div>
          </div>
        );
      case "api-swagger-ui":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <p>
              EasyChat runs on FastAPI with interactive OpenAPI documentation generated automatically at runtime:
            </p>
            <div className="flex flex-wrap gap-2.5">
              <a
                href={docsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-semibold cursor-pointer"
              >
                <span>Open Swagger UI</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
              <a
                href={redocUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 text-zinc-800 rounded-lg hover:bg-zinc-50 text-xs font-semibold cursor-pointer"
              >
                <span>Open ReDoc</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
            </div>
          </div>
        );
      case "api-agents-list":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">GET</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/ai-agents</code>
            </div>
            <p>Retrieves all AI agents created within the active workspace.</p>
            <pre className="p-3.5 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] whitespace-pre-wrap break-words break-all">
{`curl -X GET "${apiBaseHost}/api/ai-agents" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
            </pre>
            <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-xl text-[11px] font-mono whitespace-pre-wrap break-words break-all">
{`{
  "agents": [
    {
      "id": 1,
      "name": "Customer Support Agent",
      "model": "gpt-4o",
      "instructions": "You are a helpful customer support assistant...",
      "is_active": true
    }
  ]
}`}
            </div>
          </div>
        );
      case "api-agents-create":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">POST</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/ai-agents</code>
            </div>
            <p>Creates a new autonomous AI agent.</p>
            <pre className="p-3.5 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] whitespace-pre-wrap break-words break-all">
{`curl -X POST "${apiBaseHost}/api/ai-agents" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Billing Specialist",
    "model": "gpt-4o-mini",
    "instructions": "Assist users with invoice inquiries.",
    "temperature": 0.2
  }'`}
            </pre>
          </div>
        );
      case "api-agents-get":
      case "api-agents-update":
      case "api-agents-delete":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-800">CRUD</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/ai-agents/&#123;id&#125;</code>
            </div>
            <p>Supports <code>GET</code> to fetch single agent, <code>PUT</code> to update instructions or model, and <code>DELETE</code> to soft-delete.</p>
          </div>
        );
      case "api-chat-send":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">POST</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/chat</code>
            </div>
            <p>Dispatches user message into agent pipeline. Supports both standard JSON response and realtime Server-Sent Events (SSE) token stream.</p>
            <pre className="p-3.5 bg-zinc-900 text-zinc-100 rounded-xl font-mono text-[11px] whitespace-pre-wrap break-words break-all">
{`curl -X POST "${apiBaseHost}/api/chat" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "message": "Where is my order #1042?",
    "session_id": "sess_8f91c0e3",
    "ai_agent_id": 1,
    "stream": true
  }'`}
            </pre>
          </div>
        );
      case "api-chat-history":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">GET</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/chat/history/&#123;session_id&#125;</code>
            </div>
            <p>Fetches chronological message transcripts with roles (user, assistant, tool), timestamps, and token counts.</p>
          </div>
        );
      case "api-kb-upload":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">POST</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/documents/upload</code>
            </div>
            <p>Uploads PDF, DOCX, TXT, or MD documents with automated background text extraction and vector chunk embedding.</p>
          </div>
        );
      case "api-kb-search":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">POST</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/knowledge-base/search</code>
            </div>
            <p>Executes hybrid vector similarity search across indexed document embeddings with re-ranking.</p>
          </div>
        );
      case "api-tools-list":
      case "api-tools-create":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-800">REST</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/api-tools</code>
            </div>
            <p>Register custom REST endpoints with JSON schema parameters, dynamic headers, and optional OTP PIN security.</p>
          </div>
        );
      case "api-mcp-status":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">GET</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/mcp/servers</code>
            </div>
            <p>Queries connected Model Context Protocol (MCP) servers and lists active dynamic tool schemas.</p>
          </div>
        );
      case "api-wh-incoming":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">POST</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/webhook/incoming</code>
            </div>
            <p>Primary ingress endpoint for external applications sending messages via API key authentication.</p>
          </div>
        );
      case "api-wh-keys":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-800">KEYS</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/webhooks/keys</code>
            </div>
            <p>Generate, rotate, and revoke secret API keys for webhook authorization.</p>
          </div>
        );
      case "api-camp-list":
      case "api-camp-trigger":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-800">CAMPAIGNS</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/campaigns</code>
            </div>
            <p>Manage behavioral messaging triggers, conditions, and trigger manual campaign executions.</p>
          </div>
        );
      case "api-contacts-list":
      case "api-segments-list":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-800">CONTACTS</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/contacts</code>
            </div>
            <p>Retrieve customer identities, activity attributes, and segmented user lists.</p>
          </div>
        );
      case "api-handoff-pools":
      case "api-handoff-transfer":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-zinc-100 text-zinc-800">HANDOFF</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/human-agent-groups</code>
            </div>
            <p>Query operator groups and initiate live escalations from AI to human operators.</p>
          </div>
        );
      case "api-analytics-tokens":
      case "api-analytics-sessions":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">GET</span>
              <code className="text-xs font-mono font-semibold text-zinc-900">/api/analytics/tokens</code>
            </div>
            <p>Provides aggregated token usage, inference costs, sentiment distributions, and active chat metrics.</p>
          </div>
        );
      case "api-error-codes":
      case "api-error-format":
        return (
          <div className="space-y-4 text-xs text-zinc-600">
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <div className="p-2.5 bg-zinc-50 rounded-lg border border-zinc-200"><strong>200 OK</strong>: Request succeeded</div>
              <div className="p-2.5 bg-zinc-50 rounded-lg border border-zinc-200"><strong>400 Bad Request</strong>: Malformed JSON or input</div>
              <div className="p-2.5 bg-zinc-50 rounded-lg border border-zinc-200"><strong>401 Unauthorized</strong>: Invalid or missing token</div>
              <div className="p-2.5 bg-zinc-50 rounded-lg border border-zinc-200"><strong>403 Forbidden</strong>: Insufficient workspace role</div>
              <div className="p-2.5 bg-zinc-50 rounded-lg border border-zinc-200"><strong>404 Not Found</strong>: Resource does not exist</div>
              <div className="p-2.5 bg-zinc-50 rounded-lg border border-zinc-200"><strong>422 Validation Error</strong>: Field schema validation failed</div>
            </div>
          </div>
        );
      default:
        return (
          <p className="text-xs text-zinc-600 leading-relaxed">
            API reference details for <strong className="text-zinc-900">{sectionTitle || sectionId}</strong>.
          </p>
        );
    }
  };


  return (
    <div className="h-screen overflow-hidden bg-white text-zinc-900 font-sans flex flex-col antialiased selection:bg-zinc-200 selection:text-zinc-900">
      
      {/* ── TOP HEADER NAVBAR ── */}
      <header className="sticky top-0 z-[110] bg-white border-b border-zinc-200 shrink-0 shadow-2xs">
        <div className="w-full px-6 h-14 flex items-center justify-between gap-4">
          
          {/* Logo & Back button */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => navigate("/chat")}
              title="Back to EasyChat"
              aria-label="Back to EasyChat"
              className="text-zinc-500 hover:text-zinc-950 transition-colors p-1.5 -ml-1 rounded-lg hover:bg-zinc-100 active:scale-95 cursor-pointer"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold tracking-tight text-zinc-900">EasyChat Documentation</span>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex items-center gap-4 shrink-0" ref={searchContainerRef}>
            <div className="relative hidden md:block">
              <input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onFocus={() => setIsSearchOpen(true)}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchOpen(true);
                }}
                className="w-64 xl:w-72 h-8 bg-zinc-50 border border-zinc-200 hover:border-zinc-300 focus:border-zinc-900 focus:bg-white rounded-lg pl-8 pr-7 text-xs focus:outline-none transition-all placeholder:text-zinc-400 text-zinc-800 font-medium"
              />
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="absolute left-2.5 top-2.5 text-zinc-400"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>

              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setIsSearchOpen(false);
                  }}
                  className="absolute right-2 top-2 text-zinc-400 hover:text-zinc-700 cursor-pointer"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}

              {/* Search Results Dropdown */}
              {isSearchOpen && searchQuery.trim() && (
                <div className="absolute right-0 top-10 w-80 md:w-96 bg-white border border-zinc-200 rounded-xl shadow-lg py-2 z-[200] max-h-96 overflow-y-auto custom-scrollbar">
                  <div className="px-3.5 py-1.5 border-b border-zinc-100 flex items-center justify-between text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">
                    <span>Search Results</span>
                    <span className="text-zinc-500 lowercase">{searchResults.length} found</span>
                  </div>

                  {searchResults.length > 0 ? (
                    <div className="p-1 space-y-0.5">
                      {searchResults.map((res, i) => (
                        <button
                          key={`${res.categoryKey}-${res.topicId}-${res.sectionId}-${i}`}
                          onClick={() => handleSelectSearchResult(res)}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-zinc-50 transition-colors flex items-start gap-2.5 group cursor-pointer"
                        >
                          <div className="mt-0.5 text-zinc-400 group-hover:text-zinc-900 transition-colors shrink-0">
                            {res.type === "Topic" ? (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                              </svg>
                            ) : (
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <line x1="4" y1="9" x2="20" y2="9" />
                                <line x1="4" y1="15" x2="20" y2="15" />
                                <line x1="10" y1="3" x2="8" y2="21" />
                                <line x1="16" y1="3" x2="14" y2="21" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-zinc-900 group-hover:text-zinc-950 transition-colors truncate">
                              {res.title}
                            </p>
                            <p className="text-[10px] text-zinc-400 font-medium truncate mt-0.5">
                              {res.categoryTitle} &rsaquo; {res.topicTitle}
                            </p>
                          </div>
                          <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 shrink-0">
                            {res.type}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 px-4 text-center">
                      <p className="text-xs font-semibold text-zinc-700">No matching documentation</p>
                      <p className="text-[11px] text-zinc-400 mt-1">No results for &ldquo;{searchQuery}&rdquo;</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Category Navigation Tabs */}
        <div className="border-t border-zinc-100 hidden xl:block bg-zinc-50/50">
          <div className="w-full px-6 py-1.5">
            <nav className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar">
              {Object.entries(DOCS_DATA).map(([key, data]) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleTabChange(key)}
                    className={`relative px-3 py-1.5 text-xs font-medium transition-all whitespace-nowrap rounded-md cursor-pointer ${
                      isActive
                        ? "text-zinc-900 bg-white font-semibold shadow-2xs border border-zinc-200"
                        : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100"
                    }`}
                  >
                    {data.title}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Category Dropdown Nav */}
      <div className="xl:hidden bg-zinc-50 border-b border-zinc-200 px-6 py-2 flex items-center justify-between shrink-0">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Section</label>
        <select
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          className="text-xs bg-white border border-zinc-200 rounded-lg px-2 py-1 text-zinc-800 focus:outline-none font-semibold"
        >
          {Object.entries(DOCS_DATA).map(([key, data]) => (
            <option key={key} value={key}>
              {data.title}
            </option>
          ))}
        </select>
      </div>

      {/* ── THREE-COLUMN CONTENT LAYOUT ── */}
      <div className="flex-1 w-full flex overflow-hidden">
        
        {/* COLUMN 1: LEFT TOPICS SIDEBAR */}
        <aside className="w-60 border-r border-zinc-200 p-4 py-6 overflow-y-auto custom-scrollbar shrink-0 hidden md:block text-left">
          <div className="space-y-6">
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider block mb-3">
                {activeTabData.title}
              </span>
              <ul className="space-y-1">
                {activeTabData.topics.map((topic) => {
                  const isActive = activeSubTopicId === topic.id;
                  return (
                    <li key={topic.id}>
                      <button
                        onClick={() => {
                          if (topic.id === "data-deletion") {
                            navigate("/data-deletion");
                          } else if (location.pathname.includes("data-deletion")) {
                            navigate(`/docs?tab=${activeTab}&topic=${topic.id}`);
                          } else {
                            setActiveSubTopicId(topic.id);
                          }
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs rounded-lg transition-all cursor-pointer ${
                          isActive
                            ? "bg-zinc-900 text-white font-medium shadow-2xs"
                            : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 font-normal"
                        }`}
                      >
                        {topic.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </aside>

        {/* COLUMN 2: CENTER CONTENT AREA */}
        <main ref={mainScrollRef} className="flex-1 overflow-y-auto p-6 md:p-10 custom-scrollbar text-left">
          <div className="max-w-3xl space-y-8">
            
            {/* Main Page Title */}
            <div>
              <nav className="text-[11px] font-medium text-zinc-400 flex items-center gap-1.5 mb-1.5">
                <span>{activeTabData.title}</span>
                <span>/</span>
                <span className="text-zinc-700">{activeSubTopic?.title}</span>
              </nav>
              <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">
                {activeSubTopic?.title}
              </h1>
              <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                Documentation and configuration guide for {activeSubTopic?.title.toLowerCase()} in EasyChat.
              </p>
            </div>

            <div className="h-px bg-zinc-100" />

            {/* Sections */}
            <div className="space-y-8">
              {activeSubTopic?.sections.map((section) => (
                <section key={section.id} id={section.id} className="space-y-3 scroll-mt-20">
                  <h2 className="text-base font-semibold text-zinc-900 tracking-tight">
                    {section.title}
                  </h2>
                  
                  <div className="space-y-3 py-1">
                    {activeTab === "introduction" ? (
                      renderIntroductionContent(section.id, section.title)
                    ) : activeTab === "getting-started" ? (
                      renderGettingStartedContent(section.id, section.title)
                    ) : activeTab === "agents" ? (
                      renderAgentContent(section.id, section.title)
                    ) : activeTab === "knowledge-base" ? (
                      renderKnowledgeBaseContent(section.id, section.title)
                    ) : activeTab === "tools" ? (
                      renderToolsContent(section.id, section.title)
                    ) : activeTab === "channels" ? (
                      renderChannelsContent(section.id, section.title)
                    ) : activeTab === "campaigns" ? (
                      renderCampaignsContent(section.id, section.title)
                    ) : activeTab === "webhooks" ? (
                      renderWebhooksContent(section.id, section.title)
                    ) : activeTab === "api-reference" ? (
                      renderApiContent(section.id, section.title)
                    ) : (
                      <p className="text-xs text-zinc-600 leading-relaxed">
                        Configuration guide for <strong className="text-zinc-900">{section.title}</strong>.
                      </p>
                    )}
                  </div>
                </section>
              ))}
            </div>

            {/* Previous / Next Page Pagination */}
            <div className="pt-8 border-t border-zinc-200 mt-12">
              <div className="flex items-center justify-between gap-6">
                {prevPage ? (
                  <button
                    onClick={() => navigateToPage(prevPage)}
                    className="group flex flex-col items-start text-left transition-colors cursor-pointer"
                  >
                    <span className="text-xs font-semibold text-zinc-900 group-hover:text-zinc-600 flex items-center gap-1.5 transition-colors">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      {prevPage.topicTitle}
                    </span>
                    {prevPage.tabKey !== activeTab && (
                      <span className="text-[10px] text-zinc-400 mt-0.5 pl-5">
                        in {prevPage.tabTitle}
                      </span>
                    )}
                  </button>
                ) : (
                  <div />
                )}

                {nextPage && (
                  <button
                    onClick={() => navigateToPage(nextPage)}
                    className="group flex flex-col items-end text-right transition-colors ml-auto cursor-pointer"
                  >
                    <span className="text-xs font-semibold text-zinc-900 group-hover:text-zinc-600 flex items-center gap-1.5 transition-colors">
                      {nextPage.topicTitle}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </span>
                    {nextPage.tabKey !== activeTab && (
                      <span className="text-[10px] text-zinc-400 mt-0.5 pr-5">
                        in {nextPage.tabTitle}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>

          </div>
        </main>

        {/* COLUMN 3: RIGHT TABLE OF CONTENTS */}
        <aside className="w-56 border-l border-zinc-200 p-6 overflow-y-auto custom-scrollbar shrink-0 hidden lg:block text-left">
          <div className="space-y-3">
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block">
              On this page
            </span>
            <ul className="space-y-2 text-xs">
              {activeSubTopic?.sections.map((section) => {
                const isActive = activeSectionId === section.id;
                return (
                  <li key={section.id}>
                    <button
                      onClick={() => handleScrollToSection(section.id)}
                      className={`w-full text-left transition-colors line-clamp-2 cursor-pointer ${
                        isActive
                          ? "text-zinc-950 font-semibold border-l-2 border-zinc-950 pl-2 -ml-[2px]"
                          : "text-zinc-500 hover:text-zinc-900"
                      }`}
                    >
                      {section.title}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

      </div>
    </div>
  );
};

export default Docs;
